import type { ReactNode } from 'react';
import { useRouter } from '@tanstack/react-router';
import Journeys from '@/pages/journeys';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { generatePath } from '@/services/router/generate-path';
import { JourneysHandoffView } from '@/services/router/journeys-handoff-view';
import { MigrationRouteView } from '@/services/router/migration-route-view';
import { useMigrationHandoffHref } from '@/services/router/use-migration-handoff-href';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { ProtectedRoute } from '@/services/router/components/protected-route';

type ImportsListRouteViewProps = {
  /** The migrated page, mounted only once the flag is on. */
  children: ReactNode;
};

/**
 * The legacy `/journeys/imports` URL — what the embedded app and every existing link point at.
 *
 * Only the index. The upload screen and the setup wizard under `/journeys/imports` are still the
 * embedded app's, and keep falling through to the catch-all.
 */
export function JourneysImportsHandoff() {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.CsvImportsMigration);
  const router = useRouter();
  const migratedHref = useMigrationHandoffHref(
    (workspaceId) => router.buildLocation({ to: '/workspaces/$workspaceId/imports', params: { workspaceId } }).href,
  );

  return (
    <JourneysHandoffView isMigrated={isMigrated} migratedHref={migratedHref}>
      <Journeys />
    </JourneysHandoffView>
  );
}

/**
 * The migrated `/imports` URL. Flag on → this app owns the page. Flag off → the URL has no
 * embedded counterpart (the embedded app only serves the `journeys` form), so hand back to the
 * twin it does serve — the exact inverse of `JourneysImportsHandoff`. The two can't loop, because
 * each fires in the opposite flag state.
 *
 * The embedded app gates `imports.index` on `journeys/imports/view` and sends a denied viewer to
 * the journeys home; `ProtectedRoute` is the equivalent here.
 */
export function ImportsListRouteView({ children }: ImportsListRouteViewProps) {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.CsvImportsMigration);
  const router = useRouter();
  const legacyHref = useMigrationHandoffHref(
    (workspaceId) =>
      router.buildLocation({ to: '/workspaces/$workspaceId/journeys/imports', params: { workspaceId } }).href,
  );
  // Readiness gates on the resolved workspace, not the URL segment: the page's reads go through
  // `requireWorkspaceId`, which throws while the workspaces list is still loading.
  const workspace = useCurrentWorkspace();

  return (
    <MigrationRouteView isMigrated={isMigrated} legacyHref={legacyHref} isReady={workspace != null}>
      <ProtectedRoute
        permission="journeys/imports/view"
        redirectTo={generatePath('/workspaces/:workspaceId/journeys', { workspaceId: workspace?.id ?? 'last' })}
      >
        {children}
      </ProtectedRoute>
    </MigrationRouteView>
  );
}
